import { useEffect, useState } from 'react';
import { supabase, emailForUsername, USERNAME_RULE } from './supabase.js';

/**
 * Hesap: kayıt, giriş, oturum.
 *
 * Oyuncu yalnızca kullanıcı adı ve şifre giriyor; e-posta eşlemesi
 * `supabase.ts` içinde gizli. Görünen ad ayrı tutuluyor (masada bu yazıyor),
 * kullanıcı adı ise küçük harfli ve benzersiz — arkadaş eklemek buna dayanacak.
 */

export interface Account {
  userId: string;
  username: string;
  displayName: string;
}

/** Supabase'in İngilizce hata metinleri oyuncuya gösterilmiyor. */
function friendlyError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('already registered') || lower.includes('already been registered')) {
    return 'Bu kullanıcı adı alınmış.';
  }
  if (lower.includes('invalid login')) return 'Kullanıcı adı ya da şifre hatalı.';
  if (lower.includes('password')) return 'Şifre en az 6 karakter olmalı.';
  return 'Bir şeyler ters gitti, tekrar dene.';
}

function validate(username: string, password: string): string | null {
  if (!USERNAME_RULE.test(username.trim().toLowerCase())) {
    return 'Kullanıcı adı 3-20 karakter olmalı: küçük harf, rakam ve alt çizgi.';
  }
  if (password.length < 6) return 'Şifre en az 6 karakter olmalı.';
  return null;
}

export async function signUp(
  username: string,
  password: string,
  displayName: string,
): Promise<string | null> {
  if (!supabase) return 'Hesap sistemi bu kopyada kapalı.';
  const problem = validate(username, password);
  if (problem) return problem;

  const clean = username.trim().toLowerCase();
  const { error } = await supabase.auth.signUp({
    email: emailForUsername(clean),
    password,
    // Profil tetikleyicisi bu alanları okuyor: kullanıcı adı olduğu gibi kalsın.
    options: { data: { username: clean, display_name: displayName.trim() || clean } },
  });
  return error ? friendlyError(error.message) : null;
}

export async function signIn(username: string, password: string): Promise<string | null> {
  if (!supabase) return 'Hesap sistemi bu kopyada kapalı.';
  const { error } = await supabase.auth.signInWithPassword({
    email: emailForUsername(username),
    password,
  });
  return error ? friendlyError(error.message) : null;
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}

/**
 * Kullanıcı adını değiştirir.
 *
 * İKİ yeri birden güncellemek gerekiyor: profil satırı (masada ve tabloda
 * görünen) ve oturumun e-postası (giriş bununla yapılıyor —
 * `ad@basgan.local`). Yalnızca profili güncellemek, oyuncunun eski adıyla
 * giriş yapmaya devam etmesi demek olurdu.
 */
export async function updateUsername(username: string): Promise<string | null> {
  if (!supabase) return 'Hesap sistemi bu kopyada kapalı.';
  const clean = username.trim().toLowerCase();
  if (!USERNAME_RULE.test(clean)) {
    return 'Kullanıcı adı 3-20 karakter olmalı: küçük harf, rakam ve alt çizgi.';
  }

  const { data: session } = await supabase.auth.getSession();
  const userId = session.session?.user.id;
  if (!userId) return 'Oturum bulunamadı.';

  // Önce profil: benzersizlik kısıtı burada, çakışma varsa e-postaya hiç
  // dokunmadan dönüyoruz.
  const { error: profileError } = await supabase
    .from('profiles')
    .update({ username: clean })
    .eq('id', userId);
  if (profileError) {
    return profileError.code === '23505' ? 'Bu kullanıcı adı alınmış.' : friendlyError(profileError.message);
  }

  const { error: authError } = await supabase.auth.updateUser({
    email: emailForUsername(clean),
    data: { username: clean },
  });
  return authError ? friendlyError(authError.message) : null;
}

/** Masada görünen adı değiştirir — kullanıcı adından bağımsız. */
export async function updateDisplayName(displayName: string): Promise<string | null> {
  if (!supabase) return 'Hesap sistemi bu kopyada kapalı.';
  const clean = displayName.trim();
  if (clean.length < 1 || clean.length > 24) return 'Görünen ad 1-24 karakter olmalı.';

  const { data: session } = await supabase.auth.getSession();
  const userId = session.session?.user.id;
  if (!userId) return 'Oturum bulunamadı.';

  const { error } = await supabase.from('profiles').update({ display_name: clean }).eq('id', userId);
  return error ? friendlyError(error.message) : null;
}

export async function updatePassword(password: string): Promise<string | null> {
  if (!supabase) return 'Hesap sistemi bu kopyada kapalı.';
  if (password.length < 6) return 'Şifre en az 6 karakter olmalı.';
  const { error } = await supabase.auth.updateUser({ password });
  return error ? friendlyError(error.message) : null;
}

/**
 * Oyun sunucusuna verilecek kimlik belgesi.
 *
 * Durable Object bunu Supabase'in JWKS'iyle doğruluyor; yani masadaki kimlik
 * istemcinin beyanı değil, imzalı token. Süresi dolmuşsa istemci kitaplığı
 * kendisi yeniliyor.
 */
export async function accessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Oturum durumu — girişte ve çıkışta kendini güncelliyor. */
export function useAccount(): { account: Account | null; loading: boolean } {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(supabase !== null);

  useEffect(() => {
    if (!supabase) return;
    let disposed = false;

    const read = async (userId: string | undefined) => {
      if (!userId) {
        if (!disposed) setAccount(null);
        return;
      }
      const { data } = await supabase!
        .from('profiles')
        .select('username, display_name')
        .eq('id', userId)
        .maybeSingle();
      if (disposed) return;
      setAccount({
        userId,
        username: data?.username ?? '',
        displayName: data?.display_name ?? data?.username ?? 'Oyuncu',
      });
    };

    void supabase.auth.getSession().then(async ({ data }) => {
      await read(data.session?.user.id);
      if (!disposed) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      void read(session?.user.id);
    });

    return () => {
      disposed = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { account, loading };
}
